// A self-contained web page with everything the export includes: a chart of the whole tree
// (every card links to that person's entry and back), a directory of everyone with their
// relationships, life events, groups, sources, media, and to-dos, and sections listing the
// groups, sources, media, and to-dos themselves. One file that opens in any browser, offline.
// A few lines of script add a search box; without them everything still reads.

import { ageOf, ageText } from '../../model/countdown';
import { dateSortKey, formatDate } from '../../model/dates';
import { graphOf } from '../../model/graph';
import { displayName, eventTitle, genderLabel, initials, lifeSpanLong, nameToString, sortedPeople, visibleNames } from '../../model/people';
import { citationsAbout, sortTodos, sourceLabel } from '../../model/research';
import type { Id, MediaItem, Source, Todo, Tree } from '../../model/types';
import { homeRelations, relativesOf, type Linked } from '../../io/describe';
import { avatarColor } from '../components/common';
import { GROUP_KIND_LABELS, MEDIA_KIND_LABELS, TODO_PRIORITY_LABELS, TODO_STATUS_LABELS } from '../labels';
import type { ChartSvg } from './chart';

const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
const plural = (n: number, one: string, many = one + 's') => `${n.toLocaleString('en-US')} ${n === 1 ? one : many}`;
/** A to-do's due date (YYYY-MM-DD) as "Mar 3, 2026". */
const dueText = (due: string) => {
  const [y, m, d] = due.split('-').map(Number);
  return y && m && d ? new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : due;
};
const webLink = (url: string) => (/^https?:\/\//i.test(url) ? `<a href="${esc(url)}" rel="noopener noreferrer">${esc(url)}</a>` : esc(url));

export type Appearance = 'auto' | 'light' | 'dark';

export interface HtmlOptions {
  /** CSS tokens (paletteTokens) for the light and dark versions. */
  light: Record<string, string>;
  dark: Record<string, string>;
  /** Light, dark, or following the reader's device. */
  appearance: Appearance;
  /** Living people were turned into "Living"; the page says so. */
  privatized?: boolean;
  date?: Date;
}

/** Every token as a CSS variable. The chart (drawn with CSS_VAR_COLORS) uses them too, so it follows the page. */
const vars = (c: Record<string, string>) =>
  Object.entries(c)
    .filter(([k]) => k !== '--focus-ring')
    .map(([k, v]) => `${k}: ${v};`)
    .join(' ');

/**
 * `tree` should already be redacted. `chart` should be drawn with CSS_VAR_COLORS and `links`,
 * so its cards follow the page's colors and link to each person.
 */
export function htmlPage(tree: Tree, chart: ChartSvg | null, opts: HtmlOptions): string {
  const g = graphOf(tree);
  const home = homeRelations(tree);
  const now = opts.date ?? new Date();
  const link = (id: Id) => (tree.people[id] ? `<a href="#p-${esc(id)}">${esc(displayName(tree.people[id]))}</a>` : '');
  const linked = (l: Linked) => link(l.id) + (l.note ? ` <span class="muted">(${esc(l.note)})</span>` : '');
  const row = (label: string, html: string[]) => (html.length ? `<div class="rel"><span>${label}</span> ${html.join(', ')}</div>` : '');
  const list = (label: string, items: Linked[]) => row(label, items.map(linked));
  const sources = Object.values(tree.sources ?? {});
  const media = Object.values(tree.media ?? {}).sort((a, b) => dateSortKey(a.date) - dateSortKey(b.date) || a.title.localeCompare(b.title));
  const todos = sortTodos(Object.values(tree.todos ?? {}));
  const cited = new Map<Id, Set<Id>>();
  const mediaOf = new Map<Id, MediaItem[]>();
  for (const m of media) for (const x of m.people) mediaOf.set(x.personId, [...(mediaOf.get(x.personId) ?? []), m]);
  const todosOf = new Map<Id, Todo[]>();
  for (const t of todos) for (const id of t.people) todosOf.set(id, [...(todosOf.get(id) ?? []), t]);

  const people = sortedPeople(tree).map((p) => {
    const r = relativesOf(tree, p.id);
    const otherNames = visibleNames(p).slice(1).map((n) => esc(nameToString(n, { nickname: true })));
    // Only there when the export was told to include them (the tree is redacted first).
    const hiddenNames = p.names.filter((n) => !visibleNames(p).includes(n)).map((n) => esc(nameToString(n)));
    const age = ageOf(p, now);
    const facts = [lifeSpanLong(p), age ? (age.atDeath ? `died at ${ageText(age)}` : `age ${ageText(age)}`) : ''].filter(Boolean).join(', ');
    const identity = [p.gender.identity !== 'unknown' ? genderLabel(p.gender) : '', p.pronouns ?? ''].filter(Boolean).map(esc).join(' · ');
    const term = home && home.home !== p.id ? home.of(p.id) : '';
    const relation = term ? `${home!.name}’s ${term}` : '';
    const events = [...p.events]
      .sort((a, b) => dateSortKey(a.date) - dateSortKey(b.date))
      .map((e) => `<li><b>${esc(eventTitle(e))}</b>${e.date ? ` <span class="muted">${esc(formatDate(e.date, true))}</span>` : ''}${e.place ? `<br><span class="muted">${esc(e.place)}</span>` : ''}${e.note ? `<br>${esc(e.note)}` : ''}</li>`)
      .join('');
    const groups = (g.groupsOf.get(p.id) ?? []).map((grp) => {
      const role = grp.members.find((m) => m.personId === p.id)?.role;
      return `<a href="#g-${esc(grp.id)}">${esc(grp.name)}</a>${role ? ` <span class="muted">(${esc(role)})</span>` : ''}`;
    });
    // Each source once, with the pages cited from it.
    const bySource = new Map<Id, string[]>();
    for (const c of citationsAbout(tree, p.id)) {
      if (!tree.sources?.[c.citation.sourceId]) continue;
      const pages = bySource.get(c.citation.sourceId) ?? [];
      if (c.citation.page && !pages.includes(c.citation.page)) pages.push(c.citation.page);
      bySource.set(c.citation.sourceId, pages);
      if (!cited.has(c.citation.sourceId)) cited.set(c.citation.sourceId, new Set());
      cited.get(c.citation.sourceId)!.add(p.id);
    }
    const sourceLinks = [...bySource].map(([sid, pages]) => `<a href="#s-${esc(sid)}">${esc(sourceLabel(tree.sources[sid]))}</a>${pages.length ? `, ${pages.map(esc).join('; ')}` : ''}`);
    const mediaLinks = (mediaOf.get(p.id) ?? []).map((m) => `<a href="#m-${esc(m.id)}">${esc(m.title || MEDIA_KIND_LABELS[m.kind])}</a>`);
    const todoLinks = (todosOf.get(p.id) ?? []).filter((t) => t.status !== 'done').map((t) => `<a href="#t-${esc(t.id)}">${esc(t.title)}</a>`);
    const photo = p.photo ? `<img class="av" src="${esc(p.photo)}" alt="">` : `<span class="av" style="background:${avatarColor(p.id)}">${esc(initials(p))}</span>`;
    const inChart = chart?.ids.has(p.id) ? ` <a class="to-chart" href="#c-${esc(p.id)}">In the chart</a>` : '';
    return `<article id="p-${esc(p.id)}">
  <header>${photo}<div><h3>${esc(displayName(p))}${home?.home === p.id ? ' <span class="badge">Home person</span>' : ''}</h3>${facts ? `<div class="muted">${esc(facts)}</div>` : ''}${identity ? `<div class="muted">${identity}</div>` : ''}${relation ? `<div class="relation">${esc(relation)}</div>` : ''}</div></header>
  ${otherNames.length ? row('Also known as', otherNames) : ''}
  ${hiddenNames.length ? row('Hidden names', hiddenNames) : ''}
  ${list('Parents', r.parents)}
  ${list('Partners', r.partners)}
  ${list('Children', r.children)}
  ${list('Siblings', r.siblings)}
  ${list('Friends &amp; connections', r.connections)}
  ${row('Groups', groups)}
  ${events ? `<ul class="events">${events}</ul>` : ''}
  ${p.tags.length ? `<div class="tags">${p.tags.map((t) => `<span>${esc(t)}</span>`).join('')}</div>` : ''}
  ${p.notes ? `<p class="notes">${esc(p.notes)}</p>` : ''}
  ${sourceLinks.length || mediaLinks.length || todoLinks.length ? `<div class="cites">${sourceLinks.length ? `<div class="rel"><span>Sources</span> ${sourceLinks.join(' · ')}</div>` : ''}${row('Media', mediaLinks)}${row('To-dos', todoLinks)}</div>` : ''}
  ${inChart ? `<div class="foot">${inChart}</div>` : ''}
</article>`;
  });

  const groups = Object.values(tree.groups)
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((grp) => {
      const members = grp.members.filter((m) => tree.people[m.personId]).map((m) => link(m.personId) + (m.role ? ` <span class="muted">(${esc(m.role)})</span>` : ''));
      return `<li id="g-${esc(grp.id)}"><b>${esc(grp.name)}</b> <span class="muted">${esc(GROUP_KIND_LABELS[grp.kind] ?? '')} · ${plural(members.length, 'member')}</span>${grp.description ? `<div>${esc(grp.description)}</div>` : ''}<div>${members.join(', ')}</div></li>`;
    })
    .join('');

  const sourceItem = (s: Source) => {
    const facts = [s.author, s.publication, s.date ? formatDate(s.date, true) : '', s.place, s.repository, s.callNumber ? `Call number ${s.callNumber}` : ''].filter(Boolean).map((x) => esc(x!));
    const who = [...(cited.get(s.id) ?? [])];
    const copies = media.filter((m) => m.sourceIds?.includes(s.id)).map((m) => `<a href="#m-${esc(m.id)}">${esc(m.title || MEDIA_KIND_LABELS[m.kind])}</a>`);
    return `<li id="s-${esc(s.id)}"><b>${esc(s.title || 'Untitled source')}</b>${s.kind ? ` <span class="muted">${esc(s.kind)}</span>` : ''}${facts.length ? `<div class="muted">${facts.join(' · ')}</div>` : ''}${s.url ? `<div>${webLink(s.url)}</div>` : ''}${s.text ? `<blockquote>${esc(s.text)}</blockquote>` : ''}${s.notes ? `<div class="notes">${esc(s.notes)}</div>` : ''}${row('Cited for', who.map(link))}${row('Copies', copies)}</li>`;
  };
  const sourceList = sources.sort((a, b) => (a.title || '').localeCompare(b.title || '')).map(sourceItem).join('');

  const mediaList = media
    .map((m) => {
      const pic = m.thumb ? `<img class="thumb" src="${esc(m.thumb)}" alt="">` : `<span class="thumb kind">${esc(MEDIA_KIND_LABELS[m.kind])}</span>`;
      const facts = [MEDIA_KIND_LABELS[m.kind], m.date ? formatDate(m.date, true) : '', m.file?.name ?? ''].filter(Boolean).map(esc).join(' · ');
      const srcs = (m.sourceIds ?? []).filter((id) => tree.sources?.[id]).map((id) => `<a href="#s-${esc(id)}">${esc(sourceLabel(tree.sources[id]))}</a>`);
      return `<li id="m-${esc(m.id)}">${pic}<div><b>${esc(m.title || 'Untitled')}</b><div class="muted">${facts}</div>${m.description ? `<div class="notes">${esc(m.description)}</div>` : ''}${m.url ? `<div>${webLink(m.url)}</div>` : ''}${row('People', m.people.map((x) => link(x.personId)).filter(Boolean))}${row('Sources', srcs)}</div></li>`;
    })
    .join('');

  const todoList = todos
    .map((t) => {
      const facts = [TODO_STATUS_LABELS[t.status], t.priority !== 'normal' ? `${TODO_PRIORITY_LABELS[t.priority]} priority` : '', t.kind ?? '', t.due ? `Due ${dueText(t.due)}` : ''].filter(Boolean).map(esc).join(' · ');
      return `<li id="t-${esc(t.id)}" class="${t.status === 'done' ? 'done' : ''}"><b>${esc(t.title)}</b> <span class="muted">${facts}</span>${t.notes ? `<div class="notes">${esc(t.notes)}</div>` : ''}${row('About', t.people.map(link).filter(Boolean))}</li>`;
    })
    .join('');

  const count = Object.keys(tree.people).length;
  const stats = [
    plural(count, 'person', 'people'),
    groups ? plural(Object.keys(tree.groups).length, 'group') : '',
    sourceList ? plural(sources.length, 'source') : '',
    mediaList ? plural(media.length, 'media item') : '',
    todoList ? plural(todos.length, 'to-do') : '',
  ].filter(Boolean);
  const sections: [string, string, string][] = [
    ['groups', 'Groups', groups ? `<ul class="list">${groups}</ul>` : ''],
    ['sources', 'Sources', sourceList ? `<ul class="list">${sourceList}</ul>` : ''],
    ['media', 'Media', mediaList ? `<ul class="list media">${mediaList}</ul>` : ''],
    ['todos', 'To-dos', todoList ? `<ul class="list">${todoList}</ul>` : ''],
  ];
  const nav = [chart ? '<a href="#chart">Chart</a>' : '', '<a href="#people">People</a>', ...sections.filter(([, , html]) => html).map(([id, label]) => `<a href="#${id}">${label}</a>`)].filter(Boolean).join('');
  const root =
    opts.appearance === 'dark'
      ? `:root { ${vars(opts.dark)} color-scheme: dark; }`
      : `:root { ${vars(opts.light)} color-scheme: light; }${opts.appearance === 'auto' ? `\n  @media (prefers-color-scheme: dark) { :root { ${vars(opts.dark)} color-scheme: dark; } }` : ''}`;
  return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<meta name="generator" content="Relationships">
<title>${esc(tree.name)}</title>
<style>
  ${root}
  * { box-sizing: border-box; }
  body { margin: 0; background: var(--bg); color: var(--text); font: 15px/1.5 ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, Arial, sans-serif; }
  main { max-width: 1100px; margin: 0 auto; padding: 32px 16px 64px; }
  h1 { margin: 0 0 4px; font-size: 30px; letter-spacing: -0.01em; }
  h2 { margin: 36px 0 12px; font-size: 19px; scroll-margin-top: 64px; }
  a { color: var(--accent); }
  .muted { color: var(--text-muted); }
  nav { position: sticky; top: 0; z-index: 1; display: flex; flex-wrap: wrap; gap: 6px 16px; align-items: center; padding: 10px 0; margin-top: 12px; background: var(--bg); border-bottom: 1px solid var(--border); }
  nav a { font-weight: 600; text-decoration: none; }
  #find { margin-left: auto; min-width: 0; width: 240px; max-width: 100%; padding: 6px 10px; border: 1px solid var(--border); border-radius: 8px; background: var(--bg-elev); color: var(--text); font: inherit; }
  #found { font-size: 13px; }
  .chart { overflow: auto; border: 1px solid var(--border); border-radius: 12px; max-height: 80vh; scroll-padding: 40px; }
  .chart svg { display: block; max-width: none; }
  .chart a, .chart text[id] { scroll-margin: 96px 48px 48px; }
  /* The cards' colors are inline styles, so the highlight has to outrank them. */
  .chart a:hover .card, .chart a:focus .card { stroke: var(--accent) !important; stroke-width: 2; }
  .chart a:target .card { stroke: var(--accent) !important; stroke-width: 4; }
  .people { display: grid; grid-template-columns: repeat(auto-fill, minmax(min(100%, 320px), 1fr)); gap: 14px; }
  article { background: var(--bg-elev); border: 1px solid var(--border); border-radius: 12px; padding: 14px 16px; scroll-margin-top: 64px; min-width: 0; overflow-wrap: anywhere; }
  article:target, li:target { outline: 3px solid var(--accent); outline-offset: 2px; border-radius: 8px; }
  article header { display: flex; gap: 12px; align-items: center; margin-bottom: 8px; }
  h3 { margin: 0; font-size: 16.5px; }
  .badge { font-size: 11px; font-weight: 600; background: var(--accent-soft); border-radius: 999px; padding: 1px 8px; vertical-align: 2px; }
  .relation { font-size: 13px; font-weight: 600; color: var(--accent); }
  .av { width: 48px; height: 48px; border-radius: 50%; flex: none; display: grid; place-items: center; color: #fff; font-weight: 700; object-fit: cover; }
  .rel { font-size: 13.5px; margin: 2px 0; }
  .rel > span:first-child { color: var(--text-muted); font-size: 12px; text-transform: uppercase; letter-spacing: 0.05em; margin-right: 4px; }
  .cites { margin-top: 8px; padding-top: 8px; border-top: 1px solid var(--border); }
  .foot { margin-top: 6px; font-size: 12.5px; }
  .events { padding-left: 18px; margin: 8px 0; font-size: 13.5px; }
  .tags span { display: inline-block; background: var(--accent-soft); border-radius: 999px; padding: 1px 8px; margin: 2px 4px 0 0; font-size: 12px; }
  .notes { white-space: pre-wrap; font-size: 13.5px; }
  .list { padding-left: 18px; }
  .list li { margin-bottom: 12px; scroll-margin-top: 64px; }
  .list li.done b { text-decoration: line-through; color: var(--text-muted); }
  .media { list-style: none; padding: 0; }
  .media li { display: flex; gap: 12px; align-items: flex-start; }
  .thumb { width: 72px; height: 72px; flex: none; border-radius: 8px; object-fit: cover; background: var(--bg-sunken); display: grid; place-items: center; font-size: 11px; color: var(--text-muted); }
  blockquote { margin: 6px 0; padding-left: 10px; border-left: 3px solid var(--border); white-space: pre-wrap; font-size: 13.5px; }
  footer { margin-top: 40px; font-size: 12.5px; }
  [hidden] { display: none !important; }
  @media print {
    nav, .foot { display: none; }
    body { font-size: 12px; }
    .chart { max-height: none; overflow: visible; border: 0; }
    .chart svg { max-width: 100%; height: auto; }
    .people { display: block; }
    article { break-inside: avoid; margin-bottom: 10px; }
    a { color: inherit; text-decoration: none; }
  }
</style>
</head>
<body>
<main>
  <h1>${esc(tree.name)}</h1>
  <div class="muted">${stats.join(' · ')} · exported ${esc(now.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }))}</div>
  ${opts.privatized ? '<div class="muted">Details of living people are left out; they appear as “Living.”</div>' : ''}
  <nav>${nav}<input id="find" type="search" placeholder="Search people" aria-label="Search people" hidden><span id="found" class="muted" aria-live="polite"></span></nav>
  ${chart ? `<h2 id="chart">Family chart</h2>${chart.truncated ? `<p class="muted">The chart shows the first ${plural(chart.people, 'person', 'people')}; everyone is listed below.</p>` : ''}<div class="chart">${chart.svg}</div>` : ''}
  <h2 id="people">People</h2>
  <div class="people">
${people.join('\n')}
  </div>
  ${sections.filter(([, , html]) => html).map(([id, label, html]) => `<h2 id="${id}">${label}</h2>${html}`).join('\n  ')}
  <footer class="muted">Made with Relationships.</footer>
</main>
<script>
(function () {
  var input = document.getElementById('find'), found = document.getElementById('found');
  var cards = [].slice.call(document.querySelectorAll('.people article'));
  input.hidden = false;
  input.addEventListener('input', function () {
    var q = input.value.trim().toLowerCase(), n = 0;
    cards.forEach(function (a) { var hit = !q || a.textContent.toLowerCase().indexOf(q) >= 0; a.hidden = !hit; if (hit) n++; });
    found.textContent = q ? n + (n === 1 ? ' match' : ' matches') : '';
  });
})();
</script>
</body>
</html>
`;
}
